"use client";

import { motion, useInView } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";

const doctors = [
  {
    id: 1,
    name: "Dr Kevin",
    handle: "@kevinganteng_",
    image: "/images/ourteam/our_team_1.webp",
  },
  {
    id: 2,
    name: "Dr Sarah",
    handle: "@drsarah_med",
    image: "/images/ourteam/our_team_2.webp",
  },
  {
    id: 3,
    name: "Dr Michael",
    handle: "@dr.michael",
    image: "/images/ourteam/our_team_3.webp",
  },
  {
    id: 4,
    name: "Dr Emily",
    handle: "@emilyhealth",
    image: "/images/ourteam/our_team_4.webp",
  },
];

const TeamsSection = () => {
  const textRef = useRef(null);
  const imageRef = useRef(null);

  const isTextInView = useInView(textRef, { once: true });
  const isImageInView = useInView(imageRef, { once: true });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex === doctors.length - 1 ? 0 : prevIndex + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? doctors.length - 1 : prevIndex - 1));
  };

  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        nextSlide();
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused]);

  return (
    <section className='py-20 sm:px-6 lg:px-[130px] lg:h-[480px] bg-[#EDF9F7]'>
      <div className='container mx-auto'>
        <div className='flex flex-col md:flex-row gap-8 '>
          <div className='md:w-1/2 p-4'>
            <motion.div ref={textRef} initial={{ opacity: 0, y: 100 }} animate={isTextInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: "easeOut" }}>
              <h2 className='text-2xl sm:text-3xl font-bold text-teal-500 mb-4'>Dr Ari Teams</h2>
              <p className='text-gray-600 mb-8 lg:mb-22'>
                Our experienced medical team includes 10 General Practitioners, 10 Nurses, 5 Drivers, 1 Dentist, 2 Dental Nurses, and 2 Pharmacists—committed to delivering quality care with compassion and professionalism.
              </p>

              <div className='flex items-center gap-2  mb-6  '>
                <button onClick={prevSlide} className='bg-white p-2 rounded-full shadow-md hover:bg-gray-100' aria-label='Previous doctor'>
                  <ChevronLeft className='h-5 w-5 text-teal-500' />
                </button>
                <button onClick={nextSlide} className='bg-teal-500 p-2 rounded-full shadow-md hover:bg-teal-600' aria-label='Next doctor'>
                  <ChevronRight className='h-5 w-5 text-white' />
                </button>
              </div>
            </motion.div>
          </div>

          <div className='md:w-1/2 overflow-hidden'>
            <motion.div ref={imageRef} initial={{ opacity: 0, y: 100 }} animate={isImageInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}>
              <div className='flex transition-transform duration-500 ease-in-out lg:w-[470px] lg:gap-3 ' style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                {doctors.map((doctor) => (
                  <div key={doctor.id} className='min-w-full px-4 lg:px-0'>
                    <Image src={doctor.image} alt='Medical Team' width={600} height={350} className='rounded-lg object-cover' />
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        <div className='flex justify-center mt-12'>
          {doctors.map((_, index) => (
            <button key={index} className={`h-2 w-2 rounded-full mx-1 ${currentIndex === index ? "bg-teal-500" : "bg-gray-300"}`} onClick={() => setCurrentIndex(index)} aria-label={`Go to slide ${index + 1}`} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamsSection;
